"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/client/api/axios";

export interface City {
  id: number;
  name: string;
  state: string;
  country: string;
  currencyShortCode: string;
  currencySymbol: string;
  currencySymbolPosition: string;
  distanceMetric: string;
  paymentGateway: string | null;
  paymentModes: string[];
  walletProvider: string | null;
  walletSoftLimit: number | null;
  walletHardLimit: number | null;
  seoTitle: string | null;
  seoDescription: string | null;
  seoKeyword: string | null;
  imageUrls: string[];
  replicateCityId: number | null;
  zone: unknown;
  latitude: number | null;
  longitude: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  replicateCity?: { id: number; name: string; state: string; country: string } | null;
}

const CITIES_QUERY_KEY = ["cities"] as const;

async function fetchCities(status?: "active" | "deactivate") {
  const params = status ? { status } : {};
  const { data } = await api.get<{ success: boolean; data: City[] }>("/cities", {
    params,
  });
  return data.data ?? data;
}

async function fetchCity(id: number) {
  const { data } = await api.get<{ success: boolean; data: City }>(`/cities/${id}`);
  return data.data ?? data;
}

async function fetchCitiesByName(name: string | null) {
  if (!name?.trim()) return { success: true as const, data: [] as City[] };
  const { data } = await api.get<{ success: boolean; data: City[] }>("/cities", {
    params: { name: name.trim(), status: "active" },
  });
  return data;
}

export function useCityByNameQuery(name: string | null) {
  return useQuery({
    queryKey: ["cities", "name", name ?? ""],
    queryFn: () => fetchCitiesByName(name),
    enabled: !!name?.trim(),
  });
}

export type CityCreatePayload = {
  name: string;
  state: string;
  country: string;
  currencyShortCode: string;
  currencySymbol: string;
  currencySymbolPosition: "prefix" | "suffix";
  distanceMetric: string;
  paymentGateway?: string | null;
  paymentModes: string[];
  walletProvider?: string | null;
  walletSoftLimit?: number | null;
  walletHardLimit?: number | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeyword?: string | null;
  imageUrls?: string[];
  replicateCityId?: number | null;
  zone?: unknown;
  latitude?: number | null;
  longitude?: number | null;
};

async function addCity(payload: CityCreatePayload) {
  const { data } = await api.post<{ success: boolean; data: City }>("/cities", payload);
  return data.data ?? data;
}

async function updateCity(
  id: number,
  payload: Partial<CityCreatePayload> & { isActive?: boolean },
) {
  const { data } = await api.put<{ success: boolean; data: City }>(`/cities/${id}`, payload);
  return data.data ?? data;
}

async function deleteCity(id: number) {
  await api.delete(`/cities/${id}`);
}

async function uploadCityImage(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post<{ success: boolean; url: string }>(
    "/cities/upload-image",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

async function bulkDeleteCities(ids: number[]) {
  await api.post("/cities/bulk-delete", { ids });
}

async function toggleCityActive(id: number, isActive: boolean) {
  const { data } = await api.patch<{ success: boolean; data: City }>(`/cities/${id}`, {
    isActive,
  });
  return data.data ?? data;
}

export function useCitiesQuery(status?: "active" | "deactivate") {
  return useQuery({
    queryKey: [...CITIES_QUERY_KEY, status ?? "all"],
    queryFn: () => fetchCities(status),
  });
}

export function useCityQuery(id: number | null) {
  return useQuery({
    queryKey: [...CITIES_QUERY_KEY, id],
    queryFn: () => fetchCity(id!),
    enabled: id != null && id > 0,
  });
}

export function useAddCityMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addCity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CITIES_QUERY_KEY });
    },
  });
}

export function useUpdateCityMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Parameters<typeof updateCity>[1] }) =>
      updateCity(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: CITIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...CITIES_QUERY_KEY, data.id] });
    },
  });
}

export function useUploadCityImageMutation() {
  return useMutation({
    mutationFn: uploadCityImage,
  });
}

export function useDeleteCityMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CITIES_QUERY_KEY });
    },
  });
}

export function useBulkDeleteCitiesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkDeleteCities,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CITIES_QUERY_KEY });
    },
  });
}

export function useToggleCityActiveMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      toggleCityActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CITIES_QUERY_KEY });
    },
  });
}

export function getCityError(error: unknown): string | undefined {
  const err = error as {
    response?: {
      data?: {
        error?: string;
        code?: string;
        hint?: string;
        details?: Array< { path?: (string | number)[]; message?: string } >;
      };
      status?: number;
    };
  };
  const data = err?.response?.data;
  if (data?.error) return data.error;
  if (data?.code === "VALIDATION_ERROR" && Array.isArray(data?.details) && data.details.length > 0) {
    const messages = data.details
      .map((e: { path?: (string | number)[]; message?: string }) => {
        const path = e.path?.filter(Boolean).join(".") || "field";
        return `${path}: ${e.message ?? "Invalid"}`;
      })
      .join("; ");
    return messages || "Validation failed";
  }
  if (data?.code === "DATABASE_NOT_READY")
    return data.hint ?? "Database is being updated. Please try again later.";
  if (err?.response?.status === 401) return "Please sign in to continue.";
  return undefined;
}